import { Icon, IconSize, IconType } from '../../../../../../ui-toolkit';
import { DispatchApplicationAction, eApplicationActions, useApplicationContext } from '../../../../../../state';
import React, { useMemo } from 'react';
import { eTransactionCategory } from '../../../../../../ui-toolkit/enums/eTransactionType';
import { DisplayPartyPurpose, ePartyPurpose } from '../../../../../../ui-toolkit/enums/ePartyPurpose';
import { IParty, isBuyerBorrower } from '../../../../../../ui-toolkit/orders/interfaces/IParty';
import { notEmpty } from '../../../../../../ui-toolkit/utilities/notEmpty';

export interface SummaryButtonProps {
  children: React.ReactNode;
  text: string;
  onClick: () => void;
  purpose: ePartyPurpose;
}

export const useGetSummaryButtonProps = ({
  parties,
  transaction_category,
}: {
  parties: IParty[];
  transaction_category: eTransactionCategory;
}): SummaryButtonProps[] => {
  const [, dispatchContext] = useApplicationContext();

  return useMemo(() => {
    const buyerSummaryButtonProps = getBorrowerBuyerSummaryButtonProps({
      parties,
      transaction_category,
      dispatchContext,
    });
    const sellerSummaryButtonProps = getSellerSummaryButtonProps({ parties, transaction_category, dispatchContext });
    return [buyerSummaryButtonProps, sellerSummaryButtonProps].filter(notEmpty);
  }, [parties, transaction_category, dispatchContext]);
};

const getBorrowerBuyerSummaryButtonProps = ({
  parties,
  transaction_category,
  dispatchContext,
}: {
  parties: IParty[];
  transaction_category: eTransactionCategory;
  dispatchContext: DispatchApplicationAction;
}): SummaryButtonProps | undefined => {
  if (parties.filter(isBuyerBorrower).length >= 10) {
    return undefined;
  }
  const purpose = transaction_category === eTransactionCategory.Purchase ? ePartyPurpose.buyer : ePartyPurpose.borrower;
  const text = `Add ${DisplayPartyPurpose[purpose]}`;
  const children = <PlusIcon text={text} />;
  const onClick = () => dispatchContext({ type: eApplicationActions.StartPartyCreate, payload: purpose });

  return { text, purpose, children, onClick };
};

const getSellerSummaryButtonProps = ({
  parties,
  transaction_category,
  dispatchContext,
}: {
  parties: IParty[];
  transaction_category: eTransactionCategory;
  dispatchContext: DispatchApplicationAction;
}): SummaryButtonProps | undefined => {
  if (transaction_category !== eTransactionCategory.Purchase) {
    return undefined;
  }
  if (parties.filter(({ purpose }) => purpose === ePartyPurpose.seller).length >= 10) {
    return undefined;
  }
  const purpose = ePartyPurpose.seller;
  const text = 'Add Seller';
  const children = <PlusIcon text={text} />;
  const onClick = () => dispatchContext({ type: eApplicationActions.StartPartyCreate, payload: ePartyPurpose.seller });

  return { text, purpose, children, onClick };
};

const PlusIcon: React.FC<{ text: string }> = ({ text }) => {
  return (
    <>
      <Icon type={IconType.Plus} size={IconSize.Small} />
      {text}
    </>
  );
};
